import { Signaling } from "../net/signaling";

const catlabremote = require('catlab-remote-client');
const Webcontrol = catlabremote.Webcontrol;

export class BaseController {
	protected signaling: Signaling;
	protected pc: RTCPeerConnection | null = null;
	private pendingCandidates: RTCIceCandidate[] = [];

	async onPeerConnect() {
		// Override in subclass to handle peer connection establishment.
	}

	async onMessage(message: any) {
		if (typeof message !== 'object') return;

		switch (message.type) {
			// Host sends hello
			case 'hello?':
				this.signaling.send({ type: 'hello!' });
				break;

			// Client responds to hello
			case 'hello!':
				await this.handleHello();
				break;

			// Host sends initialize
			case 'initialize':
				await this.handleInitialize(message.pcOptions);
				break;

			// Client responds to initialize
			case 'initialized':
				await this.handleInitialized();
				break;

			// Has has send an offer
			case 'offer':
				await this.handleOffer(message.offer);
				break;

			// Client responds to offer
			case 'answer':
				await this.handleAnswer(message.answer);
				break;

			// ICE candidate (who?!)
			case 'candidate':
				this.addIceCandidate(new RTCIceCandidate(message.candidate));
				break;

			case 'bye':
				this.handleRemoteHangup();
				break;

			default:
				console.warn('Unknown message type:', message.type);
		}
	}

	private async handleHello() {
		const credentials = await Webcontrol.getIceCredentials();
		const pcOptions = { iceServers: credentials.iceServers };

		this.pc = this.newPeerConnection(pcOptions);
		// @ts-ignore
		window.pc = this.pc;
		this.pc.addEventListener('track', this.handleRemoteTrackAdded.bind(this));

		this.signaling.send({ type: 'initialize', pcOptions });
	}

	private async handleInitialize(pcOptions: RTCConfiguration) {
		this.pc = this.newPeerConnection(pcOptions);
		await this.onPeerConnect();
		
		this.processPendingCandidates();
		
		// Send an offer
		this.handleInitialized();
	}

	private async handleInitialized() {
		if (!this.pc) return;

		try {
			const offer = await this.pc.createOffer();
			await this.pc.setLocalDescription(offer);
			console.log('set setLocalDescription');

			this.signaling.send({
				type: 'offer',
				offer: offer,
			});
		} catch (error) {
			this.onCreateSessionDescriptionError(error);
		}
	}

	private async handleOffer(offer: RTCSessionDescriptionInit) {
		if (!this.pc) return;

		try {
			await this.pc.setRemoteDescription(offer);
			
			const answer = await this.pc.createAnswer();
			await this.pc.setLocalDescription(answer);
			console.log('set setLocalDescription');

			this.signaling.send({
				type: 'answer',
				answer: answer,
			});
		} catch (error) {
			this.onCreateSessionDescriptionError(error);
		}
	}

	private async handleAnswer(answer: RTCSessionDescriptionInit) {

		if (!this.pc) return;

		try {
			await this.pc.setRemoteDescription(answer);
		} catch (error) {
			console.error('Failed to set remote answer:', error);
		}
	}

	private addIceCandidate(candidate: RTCIceCandidate) {
		if (this.pc) {
			this.pc.addIceCandidate(candidate).catch((error) => {
				console.error('Error adding ICE candidate:', error);
			});
		} else {
			this.pendingCandidates.push(candidate);
		}
	}

	private processPendingCandidates() {
		if (!this.pc) return;

		this.pendingCandidates.forEach((candidate) =>
			this.pc.addIceCandidate(candidate).catch((error) => {
				console.error('Error adding buffered ICE candidate:', error);
			})
		);
		this.pendingCandidates = [];
	}

	private handleIceCandidate(event: RTCPeerConnectionIceEvent) {
		console.log('ICE candidate:', event.candidate);

		if (event.candidate) {
			this.signaling.send({
				type: 'candidate',
				candidate: event.candidate,
			});
		}
	}

	private handleRemoteHangup() {
		console.log('Remote peer disconnected. Cleaning up...');
		this.stop();
	}

	protected handleRemoteTrackAdded(event: RTCTrackEvent) {
		console.log('Remote track added:', event.streams);
		// Handle the remote track (e.g., attach to a video element).
	}

	private onCreateSessionDescriptionError(error: any) {
		console.error('Failed to create session description:', error);
	}

	private newPeerConnection(pcOptions: RTCConfiguration): RTCPeerConnection {
		
		const pc = new RTCPeerConnection(pcOptions);

		pc.addEventListener('icecandidate', this.handleIceCandidate.bind(this));
		pc.addEventListener('connectionstatechange', () => {
			console.log('Connection state:', pc.connectionState);
		});

		return pc;
	}

	stop() {
		if (this.pc) {
			this.pc.close();
			this.pc = null;
		}
		this.pendingCandidates = [];
		console.log('Peer connection stopped.');
	}
}
